import React from "react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2, Syringe, FileText, Loader2, FileCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import useTitanApp from "@/hooks/useTitanApp";
import {
  deleteConsultationAction,
  deletePrescriptionDraft,
} from "@/dispatchers/patientDispatchers";
import { Int32 } from "bson";

interface VaccineItem {
  medication?: string;
  vaccineName?: string;
  brandName?: string;
  dosage?: string;
  dose?: string;
  quantity?: string;
  duration?: string;
  schedule?: string;
  notes?: string;
  route?: string;
  site?: string;
  [key: string]: any;
}

interface VaccineDraftItemProps {
  items: any[];
  isDarkMode: boolean;
  consultationId: string;
  onDelete?: (
    actionName?: string,
    actionNumber?: string,
    vaccineName?: string,
  ) => void;
  draftVaccineCount: number;
  handleVaccinationClick: (item: any, vaccineData?: any) => void;
}

export const VaccineDraftItem = ({
  items,
  isDarkMode,
  consultationId,
  onDelete,
  handleVaccinationClick,
  draftVaccineCount,
}: VaccineDraftItemProps) => {
  const { patientState } = useTitanApp();
  const { deleteConsultationActionLoader } = patientState || {};
  const handleDeleteVaccinationDraft = async (
    action: any,
    vaccine: VaccineItem | null = null,
    actionNumber = "12",
    itemId: string = "",
  ) => {
    if (draftVaccineCount > 1) {
      const vaccineName = vaccine?.medication || vaccine?.vaccineName || "";
      try {
        await deleteConsultationAction({
          consultationID: consultationId,
          actionNumber,
          vaccineName,
        });
        if (onDelete) onDelete(action, actionNumber, vaccineName);
      } catch (error) {
        console.error("Error deleting vaccination draft:", error);
      } finally {
      }
    } else {
      try {
        await deletePrescriptionDraft({
          consultationID: consultationId,
          actionNumber,
        });
        if (onDelete) onDelete(action, actionNumber, "");
      } catch (error) {
        console.error("Error deleting vaccination draft:", error);
      } finally {
      }
    }
  };

  // Collect all unique vaccines from all draft items in the group
  const allVaccines: { vaccine: VaccineItem; action: any }[] = [];

  items?.forEach((action) => {
    try {
      const payload =
        typeof action.details?.payload === "string"
          ? JSON.parse(action.details.payload)
          : action.details || {};

      const vaccines =
        payload.vaccineItems || payload.vaccinations || payload.items || [];

      vaccines.forEach((v: VaccineItem) => {
        allVaccines.push({ vaccine: v, action });
      });
    } catch (e) {
      console.warn("Failed to parse vaccine draft payload", e);
    }
  });

  // Sort vaccines alphabetically
  allVaccines?.sort((a, b) => {
    const nameA = a.vaccine.medication || a.vaccine.vaccineName || "";
    const nameB = b.vaccine.medication || b.vaccine.vaccineName || "";
    return nameA.localeCompare(nameB);
  });

  if (allVaccines.length === 0) return null;

  return (
    <div className="mt-2 space-y-2 pb-4">
      {/* Banner */}
      <div
        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs m-3 ${
          isDarkMode
            ? "bg-gray-900 text-gray-400 border border-gray-600"
            : "bg-gray-100 text-gray-500 border border-gray-200"
        }`}
      >
        <FileCheck className="w-3.5 h-3.5" />
        Administer vaccines to generate a certificate
      </div>
      <div className={`border-t space-y-2 mt-3`}>
        {/* Vaccine List */}
        {allVaccines?.map((item, index) => {
          const { vaccine, action } = item;
          const title =
            vaccine.medication || vaccine.vaccineName || "Unknown Vaccine";
          const brand = vaccine.brandName ? ` - ${vaccine.brandName}` : "";
          const dose = vaccine.dose || vaccine.dosage || "As per schedule";
          const route = vaccine.route || "IM / SC";
          const site = vaccine.site || "Deltoid - left / right";

          return (
            <div
              key={`${action.id}-${index}`}
              className={`group relative flex items-start gap-2 p-2 m-3 border rounded-sm transition-all duration-200 cursor-pointer ${
                isDarkMode
                  ? "bg-gray-900 border-gray-700 hover:border-gray-500/50"
                  : "bg-gray-100 border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => handleVaccinationClick(action, vaccine)}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`text-xs font-mono ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    #{index + 1}
                  </span>
                  <h4
                    className={`text-xs font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                  >
                    {title}
                    {brand}
                  </h4>
                  <Badge className="text-[10px] px-1.5 py-0 bg-gray-200 text-gray-700">
                    Draft
                  </Badge>
                </div>
                <div
                  className={`text-[10px] flex flex-wrap gap-x-2 gap-y-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <p
                    className={`text-xs ml-6 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Dose: {dose}
                    {route && ` • Route: ${route}`}
                    {site && ` • Site: ${site}`}
                  </p>
                </div>
              </div>

              {onDelete && (
                <div
                  className="transition-opacity duration-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? "hover:bg-red-900/20 text-red-400"
                            : "hover:bg-red-50 text-red-600"
                        }`}
                        title="Delete draft"
                        disabled={deleteConsultationActionLoader}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Delete Vaccination Draft
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this vaccination
                          draft?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() =>
                            handleDeleteVaccinationDraft(action, vaccine, "12")
                          }
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
